import { useState, useEffect, useRef } from 'react'
import { Maximize, Minimize, Volume2, VolumeX, RefreshCw, Play, MoreVertical, Users } from 'lucide-react'
import Hls from 'hls.js'
import api from '../services/api'
import CameraPlayback from './CameraPlayback'
import LoadingShimmer from './LoadingShimmer'
import useStreamManager from '../hooks/useStreamManager'

const EmbeddedCameraStream = ({ camera, isExpanded, onToggleExpand, isMinimal = false, onStreamStatus }) => {
    const [isMuted, setIsMuted] = useState(false)
    const [error, setError] = useState(null)
    const [streamUrl, setStreamUrl] = useState(null)
    const [isLoading, setIsLoading] = useState(false)
    const [isStreamStarted, setIsStreamStarted] = useState(false)
    const [isFullscreen, setIsFullscreen] = useState(false)
    const [showPlayback, setShowPlayback] = useState(false)
    const [showDetails, setShowDetails] = useState(false)
    const [viewerCount, setViewerCount] = useState(0)
    const videoRef = useRef(null)
    const hlsRef = useRef(null)
    const containerRef = useRef(null)
    
    // Stream manager for sharing integration
    const streamManager = useStreamManager()

    const emitStatus = (status) => {
        try { onStreamStatus && onStreamStatus(camera._id, status, camera?.isOnline); } catch {}
    }

    useEffect(() => {
        if (camera && !isStreamStarted) {
            const timer = setTimeout(() => { startStream(); }, 100);
            return () => clearTimeout(timer);
        }
    }, [camera]);

    useEffect(() => {
        if (streamUrl && videoRef.current) {
            console.log('Video element and streamUrl available, initializing player in 200ms...', { streamUrl, videoElement: videoRef.current })
            const timer = setTimeout(() => { initializeVideoPlayer(); }, 200);
            return () => clearTimeout(timer);
        } else {
            console.log('Waiting for video element or streamUrl...', { streamUrl, videoRefExists: !!videoRef.current })
        }
    }, [streamUrl]);

    useEffect(() => {
        return () => {
            if (hlsRef.current) {
                hlsRef.current.destroy()
            }
        }
    }, [])

    useEffect(() => {
        if (camera && !camera.isOnline && isStreamStarted) {
            console.log('Camera went offline, stopping stream attempts:', camera.name)
            setError('Camera is offline')
            setStreamUrl(null)
            setIsStreamStarted(false)
            emitStatus('notReady')
            if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null }
        }
    }, [camera.isOnline, camera, isStreamStarted])

    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('mozfullscreenchange', handleFullscreenChange);
        document.addEventListener('MSFullscreenChange', handleFullscreenChange);
        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
            document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
            document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
        };
    }, []);

    const initializeVideoPlayer = () => {
        if (!videoRef.current || !streamUrl) {
            console.log('Cannot initialize video player:', { videoRefExists: !!videoRef.current, streamUrl })
            return
        }
        const absUrl = getAbsoluteStreamUrl(streamUrl)
        console.log('Initializing video player with URL:', absUrl)
        console.log('Video element ready:', videoRef.current)
        if (hlsRef.current) {
            hlsRef.current.destroy()
        }
        if (Hls.isSupported()) {
            const hls = new Hls({
                debug: false,
                enableWorker: true,
                lowLatencyMode: false, // Disable for stability
                // Stable buffer settings to prevent fluctuations
                maxBufferSize: 150 * 1000 * 1000, // 150MB buffer for stability
                maxBufferLength: 30, // 30 seconds buffer for stability
                maxMaxBufferLength: 60, // 60 seconds max buffer
                maxBufferHole: 0.1, // Stricter buffer hole tolerance
                backBufferLength: 15, // Keep 15 seconds in back buffer
                // Stable live streaming settings
                liveSyncDurationCount: 5, // More stable sync
                liveMaxLatencyDurationCount: 15, // Higher latency for stability
                liveBackBufferLength: 10, // Larger back buffer for stability
                // COMPLETELY DISABLE adaptive bitrate to prevent fluctuations
                abrEwmaDefaultEstimate: 10000000, // 10Mbps estimate (very high)
                abrBandWidthFactor: 2.0, // Never reduce bandwidth
                abrBandWidthUpFactor: 2.0, // Never reduce bandwidth
                abrMaxWithRealBitrate: false, // Disable real bitrate adaptation
                startLevel: 0, // Force highest quality level
                capLevelToPlayerSize: false, // Don't cap to player size
                capLevelOnFPSDrop: false, // Don't cap on FPS drops
                // Quality stabilization - COMPLETELY PREVENT switching
                abrEwmaSlowVoD: 0.99, // Extremely slow adaptation
                abrEwmaSlowLive: 0.99, // Extremely slow adaptation
                abrEwmaFastVoD: 0.99, // Extremely slow adaptation
                abrEwmaFastLive: 0.99, // Extremely slow adaptation
                // Additional ABR controls
                abrController: null, // Disable ABR controller entirely
                // Enhanced stability settings
                highBufferWatchdogPeriod: 5, // Longer watchdog period
                maxStarvationDelay: 10, // Longer starvation delay
                maxLoadingDelay: 10, // Longer loading delay
                nudgeOffset: 0.05, // Smaller nudge for stability
                nudgeMaxRetry: 12, // More retries
                maxFragLookUpTolerance: 0.1, // Stricter tolerance
                // More retries for stability
                fragLoadingMaxRetry: 10,
                manifestLoadingMaxRetry: 10,
                levelLoadingMaxRetry: 10,
                // Extended timeouts for stability
                fragLoadingTimeOut: 30000, // 30 seconds
                manifestLoadingTimeOut: 30000, // 30 seconds
                levelLoadingTimeOut: 30000, // 30 seconds
                // Disable features that can cause fluctuations
                enableSoftwareAES: true,
                enableDateRangeMetadataCues: false,
                enableEmsgMetadataCues: false,
                enableID3MetadataCues: false,
                enableWebVTT: false,
                enableIMSC1: false,
                enableCEA708Captions: false,
                // Force stable quality
                testBandwidth: false, // Disable bandwidth testing
                progressive: false // Disable progressive loading
            })
            hlsRef.current = hls
            hls.loadSource(absUrl)
            hls.attachMedia(videoRef.current)
            hls.on(Hls.Events.MANIFEST_PARSED, () => {
                console.log('Stream manifest loaded successfully for camera:', camera.name)
                console.log('Available quality levels for camera:', camera.name, hls.levels.length)
                
                emitStatus('ready')
                
                // Start video playback first, then lock quality after first frames load
                setTimeout(() => {
                    videoRef.current.play().catch((error) => {
                        console.log('Autoplay failed, but video will still load:', error)
                    })
                }, 300) // Small delay to ensure buffer is ready
            })

            // Smart quality lock: Allow initial load, then lock after video starts
            let qualityLocked = false
            let initialFramesLoaded = false
            
            // Wait for video to start playing, then lock quality
            const lockQualityAfterLoad = () => {
                if (!qualityLocked && hls && !hls.destroyed && videoRef.current && !videoRef.current.paused) {
                    console.log('Video is playing - NOW locking quality for camera:', camera.name)
                    hls.currentLevel = 0
                    hls.loadLevel = 0
                    hls.autoLevelEnabled = false
                    qualityLocked = true
                    
                    if (hls.levelController) {
                        hls.levelController.manualLevel = 0
                    }
                }
            }

            // Listen for video events to know when to lock quality
            if (videoRef.current) {
                videoRef.current.addEventListener('loadeddata', () => {
                    console.log('Video data loaded for camera:', camera.name)
                    setTimeout(lockQualityAfterLoad, 1000) // Lock after 1 second of playback
                })
                
                videoRef.current.addEventListener('playing', () => {
                    console.log('Video started playing for camera:', camera.name)
                    // Turn off loading state only when video actually starts playing
                    setIsLoading(false)
                    
                    // Update viewer count
                    const currentViewerCount = streamManager.getViewerCount(camera._id);
                    setViewerCount(currentViewerCount);
                    
                    if (!initialFramesLoaded) {
                        initialFramesLoaded = true
                        setTimeout(lockQualityAfterLoad, 2000) // Lock after 2 seconds of stable playback
                    }
                })
            }

            // Smart level switching prevention - only after quality is locked
            hls.on(Hls.Events.LEVEL_SWITCHING, (event, data) => {
                if (qualityLocked) {
                    console.log('BLOCKING quality switch for camera:', camera.name, 'attempted level:', data.level)
                    event.preventDefault && event.preventDefault()
                    hls.currentLevel = 0
                    hls.loadLevel = 0
                    hls.autoLevelEnabled = false
                } else {
                    console.log('Allowing initial quality selection for camera:', camera.name, 'level:', data.level)
                }
            })

            hls.on(Hls.Events.LEVEL_SWITCHED, (event, data) => {
                console.log('Quality level switched for camera:', camera.name, 'to level:', data.level)
                if (qualityLocked && data.level !== 0) {
                    console.log('FORCING back to highest quality level for camera:', camera.name)
                    setTimeout(() => {
                        hls.currentLevel = 0
                        hls.loadLevel = 0
                        hls.autoLevelEnabled = false
                    }, 100)
                }
            })

            // Monitor quality after it's locked
            const qualityEnforcer = setInterval(() => {
                if (hls && !hls.destroyed && qualityLocked) {
                    if (hls.currentLevel !== 0 || hls.loadLevel !== 0 || hls.autoLevelEnabled) {
                        console.log('Quality drift detected for camera:', camera.name, '- enforcing highest quality')
                        hls.currentLevel = 0
                        hls.loadLevel = 0
                        hls.autoLevelEnabled = false
                    }
                } else if (hls && hls.destroyed) {
                    clearInterval(qualityEnforcer)
                }
            }, 2000) // Check every 2 seconds after quality is locked

            // Enhanced buffer monitoring
            hls.on(Hls.Events.BUFFER_STALLED, () => {
                console.log('Buffer stalled for camera:', camera.name, '- attempting recovery')
                if (qualityLocked) {
                    hls.currentLevel = 0
                    hls.autoLevelEnabled = false
                }
            })

            hls.on(Hls.Events.BUFFER_FLUSHED, () => {
                console.log('Buffer flushed for camera:', camera.name)
                if (qualityLocked) {
                    hls.currentLevel = 0
                    hls.autoLevelEnabled = false
                }
            })

            hls.on(Hls.Events.FRAG_BUFFERED, () => {
                console.log('Fragment buffered for camera:', camera.name)
            })

            // Block any attempt to change quality from bandwidth estimation (only after locked)
            hls.on(Hls.Events.LEVEL_UPDATED, (event, data) => {
                if (qualityLocked) {
                    console.log('Level updated event for camera:', camera.name, '- maintaining highest quality')
                    hls.currentLevel = 0
                    hls.autoLevelEnabled = false
                }
            })
            hls.on(Hls.Events.ERROR, (event, data) => {
                console.error('HLS Error for camera:', camera.name, data)
                if (data.type === 'networkError' && data.details === 'manifestLoadError') {
                    setError('Camera is starting up...')
                    emitStatus('notReady')
                } else if (data.type === 'mediaError' && (data.details === 'bufferStalledError' || data.details === 'bufferSeekOverHole')) {
                    return
                } else if (data.fatal) {
                    setError('Optimizing connection...')
                    emitStatus('notReady')
                }
            })
            videoRef.current.onerror = (error) => {
                console.error('Video error occurred for camera:', camera.name, error)
                setError('Adjusting video quality...')
                emitStatus('notReady')
            }
        } else if (videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
            videoRef.current.src = absUrl
            
            // Add event listener for Safari to turn off loading when video starts playing
            videoRef.current.addEventListener('playing', () => {
                console.log('Video started playing for camera (Safari):', camera.name)
                setIsLoading(false)
            })
            
            videoRef.current.play().catch((error) => {
                console.log('Autoplay failed for Safari, but video will still load:', error)
            })
            emitStatus('ready')
        } else {
            setError('Updating video player...')
            emitStatus('notReady')
        }
    }

    const startStream = async () => {
        if (!camera) return
        setIsLoading(true)
        // Keep error state briefly to maintain shimmer, then clear after loading is set
        setTimeout(() => setError(null), 50)
        try {
            console.log('Starting stream for camera:', camera._id, camera.name)
            const res = await api.get(`/cameras/${camera._id}/stream`)
            console.log('Stream response for camera:', camera.name, res.data)
            if (res.data.data.streamUrl) {
                setStreamUrl(res.data.data.streamUrl)
                setIsStreamStarted(true)
                emitStatus('starting')
                
                // Register stream with sharing integration
                streamManager.registerStream(camera._id, {
                    url: res.data.data.streamUrl,
                    type: 'live',
                    userId: 'current_user' // TODO: Get from auth context
                });
                
                console.log('Stream URL set successfully for camera:', camera.name)
            } else {
                throw new Error('No stream URL received')
            }
        } catch (error) {
            console.error('Stream start error for camera:', camera.name, error)
            setError('Connecting to camera...')
            emitStatus('notReady')
            setIsStreamStarted(false)
            setIsLoading(false)
        }
    }

    const handleMute = () => {
        if (videoRef.current) {
            videoRef.current.muted = !isMuted
            setIsMuted(!isMuted)
        }
    }

    const handleFullscreen = async () => {
        try {
            if (!document.fullscreenElement) {
                if (containerRef.current.requestFullscreen) {
                    await containerRef.current.requestFullscreen();
                } else if (containerRef.current.webkitRequestFullscreen) {
                    await containerRef.current.webkitRequestFullscreen();
                } else if (containerRef.current.mozRequestFullScreen) {
                    await containerRef.current.mozRequestFullScreen();
                } else if (containerRef.current.msRequestFullscreen) {
                    await containerRef.current.msRequestFullscreen();
                }
            } else {
                if (document.exitFullscreen) {
                    await document.exitFullscreen();
                } else if (document.webkitExitFullscreen) {
                    await document.webkitExitFullscreen();
                } else if (document.mozCancelFullScreen) {
                    await document.mozCancelFullScreen();
                } else if (document.msExitFullscreen) {
                    await document.msExitFullscreen();
                }
            }
        } catch (error) {
            console.error('Error toggling fullscreen:', error);
        }
    }

    useEffect(() => {
        if (error && !isLoading && camera.isOnline && retryCount < maxRetries) {
            const delay = Math.min(2000 * Math.pow(1.5, retryCount), 10000) // Progressive delay: 2s, 3s, 4.5s, 6.75s, 10s
            const timer = setTimeout(() => {
                console.log('Auto-retrying stream for camera:', camera.name, 'attempt:', retryCount + 1)
                setRetryCount(prev => prev + 1)
                // Keep shimmer active during auto-retry
                setIsLoading(true)
                handleRetry()
            }, delay)
            return () => clearTimeout(timer)
        }
    }, [error, isLoading, camera.isOnline, retryCount])

    const [retryCount, setRetryCount] = useState(0)
    const maxRetries = 5

    const handleRetry = () => {
        if (!camera.isOnline) {
            console.log('Camera is offline, not retrying:', camera.name)
            return
        }
        console.log('Retrying stream for camera:', camera.name)
        // Keep loading state active to maintain continuous shimmer
        setIsLoading(true)
        setError('Reconnecting...')
        setStreamUrl(null)
        setIsStreamStarted(false)
        emitStatus('notReady')
        if (hlsRef.current) {
            hlsRef.current.destroy()
            hlsRef.current = null
        }
        startStream()
    }

    useEffect(() => {
        if (streamUrl && !error) {
            setRetryCount(0)
        }
        if (!camera.isOnline) {
            setRetryCount(0)
        }
    }, [streamUrl, error, camera.isOnline])

    const getAbsoluteStreamUrl = (url) => {
        if (!url) return ''
        if (url.startsWith('http')) return url
        const baseUrl = import.meta.env.VITE_API_STREAM_URL || import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://localhost:3667'
        return baseUrl + url
    }

    if (!camera) {
        return (
            <div className="bg-white rounded-xl shadow p-6">
                <div className="text-center text-gray-500">
                    <div className="text-4xl mb-2">📹</div>
                    <p>No camera assigned to your profile</p>
                </div>
            </div>
        )
    }

    if (isMinimal) {
        return (
            <div
                ref={containerRef}
                className="bg-black border border-gray-300 overflow-hidden shadow-xl min-h-0 h-full cursor-pointer"
                onClick={handleFullscreen}
            >
                <video 
                    ref={videoRef} 
                    className="w-full h-full object-cover" 
                    controls={false} 
                    muted={isMuted} 
                    playsInline 
                    autoPlay 
                    style={{ display: streamUrl ? 'block' : 'none' }}
                />
                {(isLoading || error || !streamUrl) && (
                    <div className="absolute inset-0 bg-black bg-opacity-90">
                        <LoadingShimmer />
                        {error && (
                            <div className="absolute bottom-4 right-4">
                                <button
                                    onClick={(e) => { e.stopPropagation(); handleRetry(); }}
                                    className="bg-blue-600 bg-opacity-90 hover:bg-blue-700 text-white px-3 py-1 rounded text-xs transition-colors flex items-center space-x-1 backdrop-blur-sm"
                                >
                                    <RefreshCw className="h-3 w-3" />
                                    <span>Refresh</span>
                                </button>
                            </div>
                        )}
                        {!error && !streamUrl && (
                            <div className="absolute bottom-4 right-4">
                                <button onClick={(e) => { e.stopPropagation(); handleRetry(); }} className="bg-blue-600 bg-opacity-90 hover:bg-blue-700 text-white px-3 py-1 rounded text-xs transition-colors flex items-center space-x-1 backdrop-blur-sm">
                                    <RefreshCw className="h-3 w-3" />
                                    <span>Start</span>
                                </button>
                            </div>
                        )}
                    </div>
                )}
                
                {/* Viewer Count */}
                {viewerCount > 1 && (
                    <div className="absolute bottom-2 left-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded flex items-center space-x-1 z-20">
                        <Users className="h-3 w-3" />
                        <span>{viewerCount}</span>
                    </div>
                )}
            </div>
        )
    }

    return (
        <div ref={containerRef} className="bg-white border border-gray-200 shadow-sm p-0 h-full cursor-pointer" onClick={handleFullscreen}>
            <div className={`${isFullscreen ? 'h-screen' : 'cutm_height'} bg-black overflow-hidden transition-all duration-300 flex-1 ${isExpanded ? 'col-span-full' : 'col-span-1'} relative`}>
                <div className="relative h-full">
                    <video 
                        ref={videoRef} 
                        className="w-full h-full object-cover" 
                        controls={false} 
                        muted={isMuted} 
                        playsInline 
                        autoPlay 
                        style={{ display: streamUrl ? 'block' : 'none' }}
                    />
                    {showPlayback && (
                        <div className="absolute inset-0 bg-black bg-opacity-75 flex items-center justify-center">
                            <CameraPlayback camera={camera} isOpen={showPlayback} onClose={() => setShowPlayback(false)} />
                        </div>
                    )}
                </div>
                {(isLoading || error || !streamUrl) && (
                    <div className="absolute inset-0 bg-black bg-opacity-90">
                        <LoadingShimmer />
                        {error && (
                            <div className="absolute bottom-4 right-4">
                                <button onClick={(e) => { e.stopPropagation(); handleRetry(); }} className="bg-blue-600 bg-opacity-90 hover:bg-blue-700 text-white px-3 py-1 rounded text-xs transition-colors flex items-center space-x-1 backdrop-blur-sm">
                                    <RefreshCw className="h-3 w-3" />
                                    <span>Refresh</span>
                                </button>
                            </div>
                        )}
                        {!error && !streamUrl && (
                            <div className="absolute bottom-4 right-4">
                                <button onClick={(e) => { e.stopPropagation(); handleRetry(); }} className="bg-blue-600 bg-opacity-90 hover:bg-blue-700 text-white px-3 py-1 rounded text-xs transition-colors flex items-center space-x-1 backdrop-blur-sm">
                                    <RefreshCw className="h-3 w-3" />
                                    <span>Start</span>
                                </button>
                            </div>
                        )}
                    </div>
                )}
                
                {/* Viewer Count */}
                {viewerCount > 1 && (
                    <div className="absolute bottom-2 left-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded flex items-center space-x-1 z-20">
                        <Users className="h-3 w-3" />
                        <span>{viewerCount}</span>
                    </div>
                )}
            </div>
        </div>
    )
}

export default EmbeddedCameraStream 